import React, { useState, useEffect, useCallback } from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp, mdiCashPlus, mdiCashCheck, mdiInformationOutline, mdiClose, mdiTrashCanOutline } from "@mdi/js";
import { toast } from "react-toastify";
import { classApi } from "../../api/classes";
import { formatCurrencyFromMinor } from "../../utils/currency";
import CustomFormInput from "../../components/forms/CustomFormInput";
import ConfirmationModal from "../../components/ConfirmationModal";
import ManualChargeModal from "../../components/classes/ManualChargeModal";
import CollectAdjustmentModal from "../../components/classes/CollectAdjustmentModal";
import "./PaymentsAuditTab.css";

const initialsOf = (name) =>
    (name || "")
        .split(" ")
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "?";

// Session-scoped photo cache so re-expanding / re-filtering doesn't refetch.
// A key maps to the data-URI string, or null once we've confirmed there's no photo.
const photoCache = {};

// Real student photo (matches the other class tabs) with an initials fallback.
// The photo endpoint resolves BOTH the student-profile photo and a linked account's
// avatar, so we always attempt the fetch (a "full" account-linked student has no
// studentProfileImageKey but does have an account photo).
function PaymentsAuditAvatar({ studentProfileId, name }) {
    const [photo, setPhoto] = useState(() => photoCache[studentProfileId] || null);
    useEffect(() => {
        if (studentProfileId in photoCache) {
            setPhoto(photoCache[studentProfileId] || null);
            return;
        }
        let cancelled = false;
        classApi
            .getStudentPhotoBase64(studentProfileId)
            .then((res) => {
                const b64 = res.ok && res.data?.imageBase64 ? res.data.imageBase64 : null;
                const src = b64 ? (b64.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`) : null;
                photoCache[studentProfileId] = src; // cache the src OR null (no re-fetch)
                if (!cancelled && src) setPhoto(src);
            })
            .catch(() => {
                photoCache[studentProfileId] = null;
            });
        return () => {
            cancelled = true;
        };
    }, [studentProfileId]);

    if (photo) {
        return (
            <div className="class-students-avatar class-students-avatar--cell">
                <img src={photo} alt="" className="class-students-avatar--img" />
            </div>
        );
    }
    return (
        <div className="class-students-avatar" aria-hidden="true">
            {initialsOf(name)}
        </div>
    );
}

// Group an already term-sorted ledger into contiguous per-term sections.
const groupLedgerByTerm = (ledger) => {
    const groups = [];
    const byKey = {};
    for (const l of ledger || []) {
        const key = l.termId || "__none__";
        if (!byKey[key]) {
            byKey[key] = { key, termName: l.termName || "No term", lines: [] };
            groups.push(byKey[key]);
        }
        byKey[key].lines.push(l);
    }
    return groups;
};

/**
 * Per-student payment history + management tab. Lists students with payment activity
 * (default: only those with an outstanding balance; a toggle reveals settled ones).
 * Each card expands to a per-term reconciled ledger — one line per transaction (a charge
 * with its settlement state) — plus square-up actions (mark paid, add adjustment).
 * The balance is a backend estimate; the frontend never recomputes it.
 */
function PaymentsAuditTab({ orgId }) {
    const [students, setStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showSettled, setShowSettled] = useState(false);
    const [search, setSearch] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [cardsById, setCardsById] = useState({});
    const [loadingCardId, setLoadingCardId] = useState(null);
    const [collectItem, setCollectItem] = useState(null);
    const [adjustmentStudent, setAdjustmentStudent] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadList = useCallback(async () => {
        try {
            const res = await classApi.getPaymentAuditList();
            if (res.ok) {
                setStudents(res.data.students || []);
            } else {
                toast.error(res.data?.message || "Failed to load payment activity.");
            }
        } catch (err) {
            toast.error("Failed to load payment activity.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const loadCard = useCallback(async (studentProfileId) => {
        setLoadingCardId(studentProfileId);
        try {
            const res = await classApi.getPaymentAudit(studentProfileId);
            if (res.ok) {
                setCardsById((prev) => ({ ...prev, [studentProfileId]: res.data }));
            } else {
                toast.error(res.data?.message || "Failed to load this student's history.");
            }
        } catch (err) {
            toast.error("Failed to load this student's history.");
        } finally {
            setLoadingCardId(null);
        }
    }, []);

    const toggleExpand = (studentProfileId) => {
        if (expandedId === studentProfileId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(studentProfileId);
        if (!cardsById[studentProfileId]) loadCard(studentProfileId);
    };

    const refetchAfterWrite = useCallback(
        async (studentProfileId) => {
            await Promise.all([loadList(), studentProfileId ? loadCard(studentProfileId) : Promise.resolve()]);
        },
        [loadList, loadCard]
    );

    // Collection (mark-paid / QR / email) is handled by CollectAdjustmentModal for a paymentId.
    const handleCollected = async (studentProfileId) => {
        setCollectItem(null);
        await refetchAfterWrite(studentProfileId);
    };

    const handleAdjustmentCreated = async (studentProfileId) => {
        setAdjustmentStudent(null);
        await refetchAfterWrite(studentProfileId);
    };

    // Delete an outstanding adjustment (raised in error). Reuses the existing resolve-prior
    // 'cancelled' path — a soft delete: the row is marked cancelled so it drops off the ledger
    // and Reconcile while preserving the audit trail. Only offered on unpaid manual rows.
    const handleDeleteAdjustment = async () => {
        if (!deleteItem) return;
        setIsDeleting(true);
        try {
            const res = await classApi.resolvePriorPayment(deleteItem.paymentId, orgId, "cancelled", "Adjustment removed");
            if (res.ok) {
                toast.success("Charge removed.");
                const sid = deleteItem.studentProfileId;
                setDeleteItem(null);
                await refetchAfterWrite(sid);
            } else {
                toast.error(res.data?.message || "Failed to remove charge.");
            }
        } catch (err) {
            toast.error("Failed to remove charge.");
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="payments-audit-tab">
                <p className="payments-audit-empty">Loading payment activity...</p>
            </div>
        );
    }

    const outstandingCount = students.filter((s) => s.estimatedOutstanding !== 0).length;
    const settledCount = students.length - outstandingCount;
    const q = search.trim().toLowerCase();
    // A search looks across ALL students with activity (regardless of balance); otherwise the
    // list respects the outstanding-by-default filter + the "show settled" toggle.
    const base = q ? students : showSettled ? students : students.filter((s) => s.estimatedOutstanding !== 0);
    const visibleStudents = q ? base.filter((s) => (s.studentName || "").toLowerCase().includes(q)) : base;

    return (
        <div className="payments-audit-tab">
            <div className="payments-audit-intro">
                <Icon path={mdiInformationOutline} size={1.1} color="var(--dl-color-gray-600)" />
                <span>
                    Payment history and balances per student. Add a charge or mark an item paid to square up.
                    Outstanding balances are an <strong>estimate</strong>.
                </span>
            </div>

            <div className="payments-audit-toolbar">
                <div className="class-students-search-wrap payments-audit-searchwrap">
                    <input
                        type="text"
                        className="class-students-search"
                        placeholder="Search students..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="class-students-search-clear"
                            onClick={() => setSearch("")}
                            aria-label="Clear search"
                        >
                            <Icon path={mdiClose} size={0.7} />
                        </button>
                    )}
                </div>
                <span className="payments-audit-count">
                    {outstandingCount} with a balance
                    {settledCount > 0 ? ` · ${settledCount} settled` : ""}
                </span>
                <CustomFormInput
                    type="checkbox"
                    variant="turquoise"
                    label="Show settled (zero balance)"
                    checked={showSettled}
                    onChange={(e) => setShowSettled(e.target.checked)}
                />
            </div>

            {visibleStudents.length === 0 ? (
                <p className="payments-audit-empty">
                    {q
                        ? "No students match your search."
                        : showSettled
                          ? "No students with payment activity."
                          : "No students with an outstanding balance. Toggle 'Show settled' to see paid history."}
                </p>
            ) : (
                visibleStudents.map((s) => {
                    const isExpanded = expandedId === s.studentProfileId;
                    const card = cardsById[s.studentProfileId];
                    const cardCurrency = card?.student?.currency || s.currency;
                    const owing = s.estimatedOutstanding > 0;
                    return (
                        <div className="content-card payments-audit-card" key={s.studentProfileId}>
                            <div
                                className="payments-audit-head"
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleExpand(s.studentProfileId)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleExpand(s.studentProfileId);
                                    }
                                }}
                            >
                                <PaymentsAuditAvatar
                                    studentProfileId={s.studentProfileId}
                                    name={s.studentName}
                                />
                                <div className="payments-audit-headinfo">
                                    <span className="payments-audit-name">{s.studentName}</span>
                                    {s.termsInWindow?.length > 0 && (
                                        <span className="payments-audit-terms">{s.termsInWindow.join(" · ")}</span>
                                    )}
                                </div>
                                <div className="payments-audit-outstanding-col">
                                    <span className="payments-audit-outstanding-label">
                                        {owing ? "Balance owed" : "Balance"}
                                    </span>
                                    {owing ? (
                                        <span className="payments-audit-outstanding-amount payments-audit-outstanding-amount--owing">
                                            {formatCurrencyFromMinor(s.estimatedOutstanding, s.currency)}
                                        </span>
                                    ) : (
                                        <span className="payments-audit-settled-pill">Settled</span>
                                    )}
                                </div>
                                <Icon
                                    path={isExpanded ? mdiChevronUp : mdiChevronDown}
                                    size={1}
                                    color="var(--dl-color-gray-600)"
                                />
                            </div>

                            {isExpanded && (
                                <div className="payments-audit-body">
                                    {loadingCardId === s.studentProfileId && !card ? (
                                        <p className="payments-audit-empty">Loading...</p>
                                    ) : card ? (
                                        <>
                                            {(!card.ledger || card.ledger.length === 0) ? (
                                                <p className="payments-audit-muted">No payment records.</p>
                                            ) : (
                                                groupLedgerByTerm(card.ledger).map((group) => {
                                                    const termBalance = group.lines
                                                        .filter((l) => l.isOutstanding)
                                                        .reduce((sum, l) => sum + l.amount, 0);
                                                    return (
                                                        <div className="payments-audit-termgroup" key={group.key}>
                                                            <div className="payments-audit-termhead">
                                                                <span className="payments-audit-termname">{group.termName}</span>
                                                            </div>
                                                            {group.lines.map((l) => (
                                                                <div className="payments-audit-ledrow" key={l.paymentId}>
                                                                    <div className="payments-audit-ledmain">
                                                                        <span className="payments-audit-leddesc">
                                                                            {l.description}
                                                                            {l.source === "manual" && (
                                                                                <span className="payments-audit-source-pill">Adjustment</span>
                                                                            )}
                                                                        </span>
                                                                        {l.classNames?.length > 0 &&
                                                                            l.description !== l.classNames.join(", ") && (
                                                                                <span className="payments-audit-ledclasses">
                                                                                    {l.classNames.join(", ")}
                                                                                </span>
                                                                            )}
                                                                        {l.isOutstanding && l.referenceCode && (
                                                                            <span className="payments-audit-ledmeta">{l.referenceCode}</span>
                                                                        )}
                                                                        {!l.isOutstanding && (
                                                                            <span className="payments-audit-ledmeta">
                                                                                {l.status === "partial-refund"
                                                                                    ? "Paid, partly refunded"
                                                                                    : l.status === "refunded"
                                                                                      ? "Refunded"
                                                                                      : "Paid"}
                                                                                {l.refundAmount
                                                                                    ? ` · refunded ${formatCurrencyFromMinor(l.refundAmount, cardCurrency)}`
                                                                                    : ""}
                                                                                {l.method ? ` · ${l.method}` : ""}
                                                                                {l.date ? ` · ${new Date(l.date).toLocaleDateString("en-AU")}` : ""}
                                                                                {l.referenceCode ? ` · ${l.referenceCode}` : ""}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span className="payments-audit-ledamount">
                                                                        {formatCurrencyFromMinor(l.amount, cardCurrency)}
                                                                    </span>
                                                                    <span className="payments-audit-ledaction">
                                                                        {l.isOutstanding ? (
                                                                            <>
                                                                                {l.source === "manual" && (
                                                                                    <button
                                                                                        className="payments-audit-delete-btn"
                                                                                        onClick={() =>
                                                                                            setDeleteItem({
                                                                                                paymentId: l.paymentId,
                                                                                                studentProfileId: s.studentProfileId,
                                                                                                description: l.description,
                                                                                                amountMinor: l.amount,
                                                                                                currency: cardCurrency,
                                                                                            })
                                                                                        }
                                                                                        aria-label="Delete charge"
                                                                                        title="Delete charge"
                                                                                    >
                                                                                        <Icon path={mdiTrashCanOutline} size={0.8} />
                                                                                    </button>
                                                                                )}
                                                                                <button
                                                                                    className="btn-turquoise payments-audit-markpaid-btn"
                                                                                    onClick={() =>
                                                                                        setCollectItem({
                                                                                            paymentId: l.paymentId,
                                                                                            studentProfileId: s.studentProfileId,
                                                                                            studentName: s.studentName,
                                                                                            amountMinor: l.amount,
                                                                                            currency: cardCurrency,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <span className="payments-audit-btn-inner">
                                                                                        <Icon path={mdiCashCheck} size={0.75} />
                                                                                        Collect Payment
                                                                                    </span>
                                                                                </button>
                                                                            </>
                                                                        ) : (
                                                                            <span className="payments-audit-paid-pill">
                                                                                {l.status === "partial-refund" ? "Partly refunded" : "Paid"}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            <div className="payments-audit-termfoot">
                                                                <span
                                                                    className={`payments-audit-termbal${
                                                                        termBalance > 0 ? " payments-audit-termbal--owing" : ""
                                                                    }`}
                                                                >
                                                                    {termBalance > 0
                                                                        ? `${formatCurrencyFromMinor(termBalance, cardCurrency)} owing`
                                                                        : "Settled"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}

                                            <div className="payments-audit-cardfooter">
                                                <div className="payments-audit-estimate">
                                                    <span className="payments-audit-estimate-label">Balance owed</span>
                                                    <span className="payments-audit-estimate-note">
                                                        Total of all unpaid charges across every term.
                                                    </span>
                                                    <span
                                                        className={`payments-audit-estimate-amount${
                                                            card.estimatedOutstanding > 0 ? " payments-audit-estimate-amount--owing" : ""
                                                        }`}
                                                    >
                                                        {formatCurrencyFromMinor(card.estimatedOutstanding, cardCurrency)}
                                                    </span>
                                                </div>
                                                <button
                                                    className="btn-purple payments-audit-addcharge-btn"
                                                    onClick={() =>
                                                        setAdjustmentStudent({
                                                            studentProfileId: s.studentProfileId,
                                                            studentName: s.studentName,
                                                            currency: cardCurrency,
                                                        })
                                                    }
                                                >
                                                    <span className="payments-audit-btn-inner">
                                                        <Icon path={mdiCashPlus} size={0.8} />
                                                        Add charge
                                                    </span>
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="payments-audit-empty">Could not load this student's history.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {adjustmentStudent && (
                <ManualChargeModal
                    isOpen={!!adjustmentStudent}
                    onClose={() => setAdjustmentStudent(null)}
                    studentProfileId={adjustmentStudent.studentProfileId}
                    studentName={adjustmentStudent.studentName}
                    currency={adjustmentStudent.currency}
                    onSuccess={() => handleAdjustmentCreated(adjustmentStudent.studentProfileId)}
                />
            )}

            {collectItem && (
                <CollectAdjustmentModal
                    isOpen={!!collectItem}
                    onClose={() => setCollectItem(null)}
                    paymentId={collectItem.paymentId}
                    orgId={orgId}
                    studentName={collectItem.studentName}
                    amountMinor={collectItem.amountMinor}
                    currency={collectItem.currency}
                    onSettled={() => handleCollected(collectItem.studentProfileId)}
                />
            )}

            {deleteItem && (
                <ConfirmationModal
                    isOpen={!!deleteItem}
                    title="Delete this charge?"
                    message={`Remove the charge "${deleteItem.description}" (${formatCurrencyFromMinor(
                        deleteItem.amountMinor,
                        deleteItem.currency
                    )})? This can't be undone. Raise a new charge if needed.`}
                    confirmText={isDeleting ? "Removing..." : "Delete charge"}
                    variant="danger"
                    onConfirm={handleDeleteAdjustment}
                    onCancel={() => setDeleteItem(null)}
                />
            )}
        </div>
    );
}

export default PaymentsAuditTab;
